import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useMyAdsList } from '@/features/Profile/sections/MyAds/hooks/useMyAdsList';
import { RESTORE_MAX_SELECTION } from '@/features/Profile/sections/MyAds/MyAdsSection.constants';
import { AdStatus } from '@/types';
import { PROLONG_ADS } from '@/api/mutations/ads.mutations';
import type { ProlongAdsResponse } from '@/api/types/ads.types';
import { GET_USER } from '@/api/queries/user.queries';
import type { GetUserResponse } from '@/api/types/user.types';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { useToastStore } from '@/store/useToastStore';
import { SuccessFilledIcon } from '@/components/UI/Icons/Icons';

type ProlongAdsVariables = {
  adIds: string[];
};

type UseMyAdsRestoreArgs = {
  shopPaidAdsBalance: number;
  onClose: () => void;
  onRestored: () => void;
};

export function useMyAdsRestore({
  shopPaidAdsBalance,
  onClose,
  onRestored,
}: UseMyAdsRestoreArgs) {
  const { t } = useTranslation();
  const { openToast } = useToastStore();
  const { ads, loading, canLoadMore, handleLoadMore, isLoadingMore } =
    useMyAdsList(AdStatus.Expired);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isConfirmOpen, toggleConfirm] = useToggleOpenState();
  const [isImpossibleOpen, toggleImpossible] = useToggleOpenState();

  const [refreshUser, { data: userData, loading: isCheckingBalance }] =
    useLazyQuery<GetUserResponse>(GET_USER, {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
    });
  const [prolongAds, { loading: isRestoring }] = useMutation<
    ProlongAdsResponse,
    ProlongAdsVariables
  >(PROLONG_ADS, { errorPolicy: 'all' });

  const selectedCount = selectedIds.length;
  const allSelected = ads.length > 0 && selectedCount === ads.length;
  const canConfirm = selectedCount > 0 && !isCheckingBalance;
  const balance =
    userData?.currentUser?.shopPaidAdsBalance ?? shopPaidAdsBalance;

  function isSelected(id: string) {
    return selectedIds.includes(id);
  }

  function toggle(id: string) {
    if (isCheckingBalance) return;

    if (isSelected(id)) {
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
      return;
    }

    if (selectedCount >= RESTORE_MAX_SELECTION) {
      openToast({
        text: t('pages.cabinet.restore_limit_reached', {
          count: RESTORE_MAX_SELECTION,
        }),
        size: 'compact',
        borderRadius: 14,
      });
      return;
    }

    setSelectedIds(prev => [...prev, id]);
  }

  function toggleSelectAll() {
    if (isCheckingBalance) return;

    if (selectedCount > 0) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds(ads.slice(0, RESTORE_MAX_SELECTION).map(ad => ad.id));
  }

  async function handleConfirmClick() {
    if (!canConfirm) return;

    const { data } = await refreshUser();
    const currentBalance = data?.currentUser?.shopPaidAdsBalance ?? 0;

    if (selectedCount > currentBalance) {
      toggleImpossible(true);
      return;
    }

    toggleConfirm(true);
  }

  async function confirmRestore() {
    if (isRestoring) return;

    const { data } = await prolongAds({ variables: { adIds: selectedIds } });

    if (!data?.prolongAds?.entity) return;

    toggleConfirm(false);
    setSelectedIds([]);
    openToast({
      text: t('pages.cabinet.restore_success'),
      icon: <SuccessFilledIcon width={20} height={20} />,
      size: 'compact',
      borderRadius: 14,
    });
    onRestored();
    onClose();
  }

  return {
    ads,
    loading,
    balance,
    canLoadMore,
    handleLoadMore,
    isLoadingMore,
    selectedCount,
    allSelected,
    canConfirm,
    isSelected,
    toggle,
    toggleSelectAll,
    handleConfirmClick,
    isConfirmOpen,
    closeConfirm: () => toggleConfirm(false),
    confirmRestore,
    isRestoring,
    isCheckingBalance,
    isImpossibleOpen,
    closeImpossible: () => toggleImpossible(false),
  };
}
